/**
 * A space on the rail: a 12px gap. In edit mode (expanded rail) it shows as a
 * dashed line you can drag, with a right-click menu to move or remove it;
 * otherwise it is only the gap.
 */
import React, { useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ContextMenuItemButtons, ContextMenuShell } from '@features/workspace';

import { spaceMenuItems, type RailActions } from './railMenus';

import type { RailNodeData } from './useRailDrag';
import type { RailLayout, RailSpace } from '@shared/navigation/railLayout';

interface SpaceItemProps {
  space: RailSpace;
  layout: RailLayout;
  editMode: boolean;
  isCollapsed: boolean;
  actions: RailActions;
}

const SPACE_DATA: RailNodeData = { kind: 'space' };

/** A gap; a draggable dashed line while editing. */
export const SpaceItem: React.FC<SpaceItemProps> = ({
  space,
  layout,
  editMode,
  isCollapsed,
  actions,
}) => {
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: space.id,
    data: SPACE_DATA,
  });
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!editMode || isCollapsed) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="h-3"
        aria-hidden="true"
        data-testid="rail-space"
      />
    );
  }
  return (
    <>
      <button
        ref={setNodeRef}
        type="button"
        style={style}
        aria-label="Space"
        data-testid="rail-space"
        onContextMenu={e => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        className="flex h-3 w-full cursor-grab items-center px-2 active:cursor-grabbing"
        {...listeners}
      >
        <span className="border-sidebar-border w-full border-t border-dashed" />
      </button>
      {menu && (
        <ContextMenuShell x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <ContextMenuItemButtons
            items={spaceMenuItems(layout, space.id, actions)}
            onClose={() => setMenu(null)}
          />
        </ContextMenuShell>
      )}
    </>
  );
};
